import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import Footer from "@/components/Footer";
import { Shield, LogOut, ExternalLink, Search, ShieldOff, Star, Clock } from "lucide-react";
import { toast } from "sonner";
import { daysUntil } from "@/config/commercial";

function StatusBadge({ active }) {
  return (
    <span
      className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${
        active
          ? "text-gold border-gold/40 bg-gold/10"
          : "text-neonpink border-neonpink/40 bg-neonpink/10"
      }`}
    >
      {active ? "Activo" : "Vencido"}
    </span>
  );
}

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/businesses");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (biz, payload) => {
    try {
      const { data } = await api.patch(`/admin/businesses/${biz.id}`, payload);
      setItems((arr) => arr.map((x) => (x.id === biz.id ? data : x)));
      toast.success("Actualizado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const filtered = items.filter((b) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return b.name.toLowerCase().includes(s) || b.slug.includes(s) || (b.owner_email || "").toLowerCase().includes(s);
  });

  const stats = {
    total: items.length,
    active: items.filter((b) => b.active).length,
    expired: items.filter((b) => !b.active).length,
  };

  return (
    <div className="min-h-screen bg-void text-neutral-100 grain flex flex-col">
      <header className="border-b border-gold/10 sticky top-0 z-10 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-neonpink/15 border border-neonpink/40 flex items-center justify-center">
              <Shield className="w-5 h-5 text-neonpink" strokeWidth={1.5} />
            </div>
            <div>
              <p className="overline text-neonpink">Super Admin</p>
              <h1 className="font-display text-xl leading-none">{user?.name || "Admin"}</h1>
            </div>
          </div>
          <Button variant="ghost" className="text-neutral-400 hover:text-neonpink" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto p-6">
        <div className="mb-8">
          <p className="overline mb-2">Panel maestro</p>
          <h2 className="font-display text-4xl md:text-5xl" data-testid="super-admin-title">
            Todos los <span className="text-gold">negocios</span>
          </h2>
        </div>

        <div className="grid grid-cols-3 gap-4 md:gap-6 mb-8">
          {[
            { l: "Total", v: stats.total, c: "#FFD700", tid: "admin-stat-total" },
            { l: "Activos", v: stats.active, c: "#00E5FF", tid: "admin-stat-active" },
            { l: "Vencidos", v: stats.expired, c: "#FF3AD2", tid: "admin-stat-expired" },
          ].map((s) => (
            <div key={s.l} data-testid={s.tid} className="bg-surface gold-border rounded-2xl p-5">
              <p className="overline mb-2">{s.l}</p>
              <p className="font-display text-4xl md:text-5xl" style={{ color: s.c }}>{s.v}</p>
            </div>
          ))}
        </div>

        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, slug o correo del dueño"
            className="pl-9 bg-surface border-gold/20 h-12"
            data-testid="admin-search"
          />
        </div>

        <div className="bg-surface gold-border rounded-2xl overflow-hidden" data-testid="admin-businesses-list">
          {loading ? (
            <div className="p-10 text-center text-neutral-500">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-neutral-500">Sin negocios.</div>
          ) : (
            <div className="divide-y divide-gold/10">
              {filtered.map((b) => (
                <div key={b.id} className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="font-display text-lg truncate">{b.name}</div>
                      <StatusBadge active={b.active} />
                    </div>
                    <div className="text-xs text-neutral-500 mt-1 truncate">
                      {b.owner_email} · <span className="text-cyan">/agenda/{b.slug}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">Activo</span>
                    <Switch
                      checked={b.raw_active !== false}
                      onCheckedChange={(v) => patch(b, { active: v })}
                      data-testid={`admin-toggle-${b.id}`}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                      <Star className="w-3 h-3 text-gold" strokeWidth={2} /> Founding
                    </span>
                    <Switch
                      checked={!!b.founding_member}
                      onCheckedChange={(v) => patch(b, { founding_member: v })}
                      data-testid={`admin-founding-${b.id}`}
                    />
                  </div>

                  {(() => {
                    const days = daysUntil(b.subscription_expires_at);
                    const hasTrial = days !== null;
                    const color = !hasTrial
                      ? "#737373"
                      : days === 0
                      ? "#FF3AD2"
                      : days <= 3
                      ? "#FF3AD2"
                      : "#FFD700";
                    return (
                      <div
                        className="text-center min-w-[80px]"
                        data-testid={`admin-days-remaining-${b.id}`}
                      >
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex items-center justify-center gap-1 mb-0.5">
                          <Clock className="w-3 h-3" strokeWidth={2} /> Restantes
                        </div>
                        {hasTrial ? (
                          <div className="font-display text-xl leading-none" style={{ color }}>
                            {days}
                            <span className="text-[10px] uppercase tracking-widest text-neutral-500 ml-1">
                              {days === 1 ? "día" : "días"}
                            </span>
                          </div>
                        ) : (
                          <div className="font-mono text-xs text-neutral-500">—</div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="w-full lg:w-56">
                    <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
                      Vence
                    </label>
                    <Input
                      type="date"
                      defaultValue={b.subscription_expires_at || ""}
                      onBlur={(e) => {
                        if ((e.target.value || "") !== (b.subscription_expires_at || "")) {
                          patch(b, { subscription_expires_at: e.target.value });
                        }
                      }}
                      className="bg-void border-gold/20 h-11"
                      data-testid={`admin-expires-${b.id}`}
                    />
                  </div>

                  <div className="flex gap-2">
                    <a
                      href={`/agenda/${b.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-view-${b.id}`}
                    >
                      <Button size="sm" variant="outline" className="border-cyan/40 text-cyan hover:bg-cyan/10 rounded-full">
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> Ver
                      </Button>
                    </a>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => patch(b, { active: false, subscription_expires_at: "2020-01-01" })}
                      className="text-neonpink hover:bg-neonpink/10 rounded-full"
                      data-testid={`admin-expire-${b.id}`}
                    >
                      <ShieldOff className="w-3.5 h-3.5 mr-1" /> Bloquear
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
